import React from 'react';
import type { UnitPriceType } from 'shared/src/domain/line-item/line-item-channels';
import { getUnitName } from 'shared/src/domain/line-item/line-item-utils';
import { useCubeQuery } from '@/components/display/use-cube-query';
import { useLineItemContext } from '@/features/line-items/line-items-context';
import type { KpiViewMode } from './line-item-daily-kpis';
import { PacingDailyKpis } from './pacing-daily-kpis';
import type { TimeFrameMode } from './time-frame-utils';

type Props = {
  timeFrameMode: Exclude<TimeFrameMode, 'last_7_days'>;
  setViewMode: (mode: KpiViewMode) => void;
};

export function PacingDeliveryKpis({ timeFrameMode, setViewMode }: Props) {
  const { lineItem } = useLineItemContext();

  const result = useCubeQuery({
    dimensions: ['line_items.unit_price_type_name', 'line_item_metrics_daily.line_item_id'],
    measures: [
      'line_item_metrics_daily.actual_units_yesterday',
      'line_item_metrics_daily.planned_units_yesterday',
      requiredDailyUnits(timeFrameMode),
      plannedDailyUnits(timeFrameMode),
      'line_item_metrics_daily.avg_actual_units_last_3_days',
      'line_item_metrics_daily.avg_actual_units_last_7_days'
    ],
    filters: [
      {
        member: 'line_item_metrics_daily.line_item_id',
        operator: 'equals',
        values: [lineItem.id]
      }
    ]
  });

  if (result.status === 'loading') {
    return (
      <PacingDailyKpis
        viewMode="delivery"
        setViewMode={setViewMode}
        loading
        unitAbbr=""
        yesterday={{ formatted: DASH, actual: null, planned: null }}
        dailyNeeded={{
          formatted: DASH,
          actual: null,
          planned: null,
          label: dailyNeededLabel(timeFrameMode)
        }}
        avg3Day={DASH}
        avg7Day={DASH}
      />
    );
  }

  if (result.status === 'error' || !result.formattedRows[0]) {
    return (
      <PacingDailyKpis
        viewMode="delivery"
        setViewMode={setViewMode}
        unitAbbr=""
        yesterday={{ formatted: DASH, actual: null, planned: null }}
        dailyNeeded={{
          formatted: DASH,
          actual: null,
          planned: null,
          label: dailyNeededLabel(timeFrameMode)
        }}
        avg3Day={DASH}
        avg7Day={DASH}
      />
    );
  }

  const formatted = result.formattedRows[0];
  const raw = result.rows[0];

  const unitAbbr = raw
    ? getUnitName(raw['line_items.unit_price_type_name'] as UnitPriceType['name']).abbr || 'units'
    : '';
  const required = requiredDailyUnits(timeFrameMode);
  const planned = plannedDailyUnits(timeFrameMode);

  return (
    <PacingDailyKpis
      viewMode="delivery"
      setViewMode={setViewMode}
      unitAbbr={unitAbbr}
      yesterday={{
        formatted: formatted['line_item_metrics_daily.actual_units_yesterday'],
        actual: raw ? Number(raw['line_item_metrics_daily.actual_units_yesterday']) : null,
        planned: raw ? Number(raw['line_item_metrics_daily.planned_units_yesterday']) : null
      }}
      dailyNeeded={{
        formatted: formatted[required],
        actual: raw ? Number(raw[required]) : null,
        planned: raw ? Number(raw[planned]) : null,
        label: dailyNeededLabel(timeFrameMode)
      }}
      avg3Day={formatted['line_item_metrics_daily.avg_actual_units_last_3_days']}
      avg7Day={formatted['line_item_metrics_daily.avg_actual_units_last_7_days']}
    />
  );
}

const DASH = '—';

type PacingTimeFrameMode = Exclude<TimeFrameMode, 'last_7_days'>;

function requiredDailyUnits(timeFrameMode: PacingTimeFrameMode) {
  switch (timeFrameMode) {
    case 'pacing_blocks':
      return 'line_items.required_daily_units_block' as const;
    case 'months':
      return 'line_items.required_daily_units_month' as const;
    case 'lifetime':
      return 'line_items.required_daily_units_lifetime' as const;
  }
}

function plannedDailyUnits(timeFrameMode: PacingTimeFrameMode) {
  switch (timeFrameMode) {
    case 'pacing_blocks':
      return 'line_items.planned_daily_units_block' as const;
    case 'months':
      return 'line_items.planned_daily_units_month' as const;
    case 'lifetime':
      return 'line_items.planned_daily_units_lifetime' as const;
  }
}

function dailyNeededLabel(timeFrameMode: PacingTimeFrameMode) {
  switch (timeFrameMode) {
    case 'pacing_blocks':
      return 'Block daily needed';
    case 'months':
      return 'Month daily needed';
    case 'lifetime':
      return 'Lifetime daily needed';
  }
}
